import { useMutation, useQuery, useQueryClient } from "react-query";

import { useAccessToken } from "hooks/useAccessToken";
import { BACKEND_DOMAIN } from "utils/constants";
import { buildRequestHeaders } from "utils/helpers";

export const buildingsQueryKey = ["buildingChatHistory"];

const useBuildingChatHistoryQuery = (buildingModelUid: string | null) => {
    const accessToken = useAccessToken();

    return useQuery({
        queryKey: [buildingsQueryKey, buildingModelUid],
        queryFn: async () => {
            if (!buildingModelUid) {
                return Promise.resolve([]);
            }

            const URL = `${BACKEND_DOMAIN}/single-building-chatbot/history/${buildingModelUid}`;
            const response = await fetch(URL, {
                method: "GET",
                headers: buildRequestHeaders(accessToken),
            });

            if (!response.ok) {
                throw new Error(`Error ${response.status}`);
            }

            return response.json();
        },
        enabled: !!buildingModelUid,
    });
};

const useClearBuildingChatHistoryMutation = () => {
    const accessToken = useAccessToken();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (buildingModelUid: string) => {
            const URL = `${BACKEND_DOMAIN}/single-building-chatbot/history/${buildingModelUid}`;
            const response = await fetch(URL, {
                method: "DELETE",
                headers: buildRequestHeaders(accessToken),
            });

            if (!response.ok) {
                throw new Error(`Error ${response.status}`);
            }

            return response.json();
        },
        onSuccess: (_, buildingModelUid) => {
            // Invalidate and refetch the chat history query
            queryClient.invalidateQueries({
                queryKey: [buildingsQueryKey, buildingModelUid],
            });
        },
    });
};

const useExportBuildingChatHistory = () => {
    const accessToken = useAccessToken();

    return useMutation({
        mutationFn: async (buildingModelUid: string) => {
            const URL = `${BACKEND_DOMAIN}/single-building-chatbot/history/${buildingModelUid}/export`;
            const response = await fetch(URL, {
                method: "GET",
                headers: buildRequestHeaders(accessToken),
            });

            if (!response.ok) {
                throw new Error(`Error ${response.status}`);
            }

            return response.blob();
        },
    });
};

/** Parsed JSON from `/agent-results/export` */
export interface AgentResultArtifact {
    agent: string;
    data: Record<string, unknown>;
}

type ExportAgentArtifactResponseType = "blob" | "json";

interface ExportAgentArtifactMutationArgs {
    buildingModelUid: string;
    runUid: string;
    agent: string;
    responseType: ExportAgentArtifactResponseType;
}

const useExportAgentArtifactMutation = () => {
    const accessToken = useAccessToken();

    return useMutation({
        mutationFn: async ({
            buildingModelUid,
            runUid,
            agent,
            responseType,
        }: ExportAgentArtifactMutationArgs): Promise<Blob | AgentResultArtifact> => {
            const params = new URLSearchParams({
                building_model_uid: buildingModelUid,
                run_uid: runUid,
                agent,
            });
            const URL = `${BACKEND_DOMAIN}/single-building-chatbot/agent-results/export?${params}`;
            const response = await fetch(URL, {
                method: "GET",
                headers: buildRequestHeaders(accessToken),
            });

            if (!response.ok) {
                throw new Error(`Error ${response.status}`);
            }

            if (responseType === "blob") {
                return response.blob();
            }

            return response.json();
        },
    });
};

export {
    useBuildingChatHistoryQuery,
    useClearBuildingChatHistoryMutation, useExportAgentArtifactMutation, useExportBuildingChatHistory
};
